/*****************************************************************
 *  ZOHO BRIDGE PANEL — THE "LOG" TAB                              *
 *  File: apps/web/src/pages/bridge/LogTab.js                      *
 *  What it does: every delivery, decision and key change for this *
 *  company, newest first, in plain words. The log is append-only; *
 *  this screen only reads it.                                     *
 *  Reads: GET /log.  Writes: nothing.                             *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { BRIDGE_WORDS, LOG_ACTION_NAMES } from './bridgeWords';
import { LoadState, ToneChip, Pager, formatWhen } from './bridgeParts';
import { fetchLog } from './bridgeApi';

// How each result looks (word + icon + colour, never colour alone).
const RESULT_LOOKS = {
  ok: { tone: 'ok', icon: 'check', word: 'Done' },
  rejected: { tone: 'bad', icon: 'cross', word: 'Refused' },
  failed: { tone: 'warn', icon: 'alert', word: 'Needs a person' },
  waiting: { tone: 'warn', icon: 'clock', word: 'Waiting for its customer' },
};

/* ---- describeLogRow(logRow) -- one line of plain words ---- */
function describeLogRow(logRow) {
  const counts = logRow.counts;
  const detail = logRow.detail || {};

  if (counts && counts.received !== undefined) {
    return counts.received + ' received: ' + counts.new + ' new, '
      + (counts.updated || 0) + ' updated in place, '
      + counts.unchanged + ' unchanged, ' + counts.errors + ' with problems.';
  }

  if (counts && counts.total !== undefined) {
    return (counts.done !== undefined ? counts.done + ' of ' : '') + counts.total + ' records'
      + (counts.applied !== undefined ? ': ' + counts.applied + ' put into Butler' : '') + '.';
  }

  if (detail.why) {
    return '"' + detail.why + '"' + (detail.reason ? ' — ' + detail.reason : '');
  }

  return detail.reason || detail.name || '';
}

export default function LogTab() {
  const [logRows, setLogRows] = useState([]);            // the log lines (one page)
  const [loadState, setLoadState] = useState('loading'); // loading / ready / error
  const [pageNumber, setPageNumber] = useState(1);       // which page (defect 372)
  const [pageInfo, setPageInfo] = useState({ total: 0, pageSize: 50 }); // the total and page size

  /* ---- loadLog() -- one page, newest first ---- */
  const loadLog = useCallback(async () => {
    setLoadState('loading');

    const answer = await fetchLog(pageNumber);

    if (!answer.ok || !answer.data) {
      setLoadState('error');
      return;
    }

    setLogRows(answer.data.rows || []);
    setPageInfo({ total: answer.data.total, pageSize: answer.data.pageSize });
    setLoadState('ready');
  }, [pageNumber]);

  useEffect(() => {
    loadLog();
  }, [loadLog]);

  return (
    <section aria-labelledby="zb-tab-log-title">
      <h2 id="zb-tab-log-title" className="zb-visually-hidden">{BRIDGE_WORDS.tabLog}</h2>

      {loadState !== 'ready' ? <LoadState state={loadState} onRetry={loadLog} /> : null}
      {loadState === 'ready' && logRows.length === 0 ? <LoadState state="empty" emptyWord={BRIDGE_WORDS.logEmpty} /> : null}

      {loadState === 'ready' && pageInfo.total > 0 ? (
        <Pager page={pageNumber} pageSize={pageInfo.pageSize} total={pageInfo.total} onPage={setPageNumber} />
      ) : null}

      <ul className="zb-list">
        {logRows.map((oneRow) => {
          const look = RESULT_LOOKS[oneRow.result] || RESULT_LOOKS.ok;

          return (
            <li className="zb-list-row zb-list-row-stack" key={oneRow.id}>
              <div className="zb-list-main">
                <strong>{LOG_ACTION_NAMES[oneRow.action] || oneRow.action}</strong>
                <ToneChip tone={look.tone} icon={look.icon} word={look.word} />
              </div>
              <p className="zb-small">{formatWhen(oneRow.happenedAt)} · {describeLogRow(oneRow)}</p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
